import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import {
  ApiError, addNetwork, approveDevice, closeEnrollWindow, declineDevice,
  listNetworks, mintEnrollCode, openEnrollWindow, renameFieldDevice,
  revokeEnrollCode, revokeNetwork, rotateEnrollCode, unrollDevice,
  type EnrollCode, type EnrollNetwork, type EnrollWindowState,
  type FieldDevice, type MemberRole, type Me, type MintedCode, type OrgDetail,
} from '../api'
import { PageHead } from '../components/Shell'
import { Empty, ErrorBox, Field, Rows } from '../components/bits'
import { Qr } from '../components/Qr'
import { canonical, grouped } from './Koble'
import { invalidate, useApi } from '../useApi'
import { useT } from '../lang'

/* Connecting the people who do the counting, from a desk.
 *
 * Two lists that look similar and are not the same kind of object at all, which
 * is why they are labelled rather than merged:
 *
 *   CODES are what you hand out. A code opens one endpoint, is spent the moment
 *   it admits a device, and is never read again on any other request. That is
 *   what makes rotating one an ordinary afternoon's work: nobody already
 *   connected notices.
 *
 *   DEVICES are what came in. Unrolling one stops it at its very next request
 *   and leaves everything it recorded exactly where it is, still attributed to
 *   it -- because a count with nobody's name on it is worse than a count from
 *   a tablet that has since been sold.
 */

const ROLE_WORD: Record<MemberRole, string> = {
  company_manager: 'Company manager',
  area_manager: 'Area manager',
  site_manager: 'Site manager',
  ship: 'Vessel',
  subsupplier: 'Subsupplier',
}

export function Enheter({ me }: { me: Me }) {
  const t = useT()
  const orgs = me.orgs ?? []
  const [orgId, setOrgId] = useState(orgs[0]?.org_id ?? '')

  if (orgs.length === 0) {
    return <Empty title={t('No company')}>
      {t('This account is not a member of a company.')}
    </Empty>
  }

  return (
    <>
      <PageHead
        title={t('Devices')}
        lead={t('Phones and tablets that register counts, and the codes that let them in.')}
      />
      {orgs.length > 1 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <Field label="Company">
            <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              {orgs.map((o) => (
                <option key={o.org_id} value={o.org_id}>{o.name}</option>
              ))}
            </select>
          </Field>
        </div>
      )}
      <Devices orgId={orgId} />
      <EnrollWindow orgId={orgId} />
      <Codes orgId={orgId} />
      <Networks orgId={orgId} />
    </>
  )
}

/* ------------------------------------------------------------------ codes */

/* ------------------------------------------------------------- the window */

/* The door, propped open for a few hours.
 *
 * The third way a device gets in, and the one that exists because the second
 * one has an honest limit: a registered network admits without asking anybody,
 * and it only works behind a fixed address. On mobile data -- which is where a
 * crew actually is -- a phone on the walkway and a phone in Oslo look the same
 * from the receiver, so a company that registered its 4G range would have
 * registered most of western Norway.
 *
 * So instead of widening WHERE, this narrows WHEN. Its own card rather than a
 * control inside Codes, because it is not a property of any one code: it
 * applies to every code the company has, and putting it on one of them would
 * say the opposite.
 */
export function EnrollWindow({ orgId }: { orgId: string }) {
  const t = useT()
  const path = `/v1/orgs/${encodeURIComponent(orgId)}/enroll-codes`
  // The same cache key Codes uses, so this paints from cache and the two can
  // never show a different answer about the same door.
  const q = useApi<{ items: EnrollCode[]; window?: EnrollWindowState }>(
    orgId ? path : null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [letIn, setLetIn] = useState<string[] | null>(null)
  const [skipped, setSkipped] = useState<string[]>([])
  // Re-rendered on a timer so "closes in 41 minutes" is not a number that was
  // true when the page loaded. A minute is the resolution the sentence has.
  const [, tick] = useState(0)

  const w = q.data?.window
  const open = !!w?.open && !!w.open_until && new Date(w.open_until) > new Date()
  /* A RECEIVER THAT PREDATES THIS FEATURE SHOWS NO CARD AT ALL.
   *
   * The two repos deploy independently -- that split is the point of them --
   * so this bundle can be live for a few minutes against a receiver that has
   * never heard of an enrollment window. `window` absent from the codes
   * response is exactly that receiver, and it is distinguishable from a closed
   * door, which sends `open: false`.
   *
   * Rendering the buttons anyway would offer a control that answers 404, which
   * is the one outcome worse than the feature not being there: somebody presses
   * it while handing out phones, gets an error, and stops trusting the screen.
   * Hidden until the other half is deployed, on the same rule the header
   * comment on Me states -- read a new field with a default, never bare.
   *
   * `cold` is checked first so the card does not flash away and back on load.
   *
   * BELOW EVERY HOOK. It sat above the effect, so the card rendered one hook
   * fewer the moment the codes arrived without `window` than it had while they
   * were loading, and React threw #300 and took the whole Operators page with
   * it -- against exactly the older receiver this return exists for. */
  useEffect(() => {
    if (!open) return
    const id = setInterval(() => tick((n) => n + 1), 30_000)
    return () => clearInterval(id)
  }, [open])

  // A refused read hides it too: with no state the hour buttons would still
  // draw, and pressing one would fail. Codes shows the error for the same path.
  if (q.error || (!q.cold && q.data && !w)) return null

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      const r = await fn() as { admitted?: string[]; skipped?: string[] }
      setLetIn(r.admitted ?? null)
      setSkipped(r.skipped ?? [])
      invalidate(path)
      invalidate(`/v1/orgs/${encodeURIComponent(orgId)}/devices`)
      q.reload()
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.code)
        : t('Could not reach the receiver.'))
    } finally {
      setBusy(false)
    }
  }

  const closesIn = () => {
    if (!w?.open_until) return ''
    const mins = Math.max(0, Math.round(
      (new Date(w.open_until).getTime() - Date.now()) / 60000))
    if (mins < 60) return `${mins} ${t('minutes')}`
    const h = Math.floor(mins / 60)
    return `${h} ${h === 1 ? t('hour') : t('hours')} ${mins % 60} ${t('minutes')}`
  }

  const hours = [1, 2, 4, 8].filter((h) => h <= (w?.max_hours ?? 12))

  return (
    <section className="card stack" style={{ gap: 12, marginBottom: 20 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>{t('Open connection')}</h2>
      <p className="faint" style={{ margin: 0, fontSize: 13 }}>
        {t('While this is open, anyone who types one of your codes is let straight in and nobody is asked. Use it while you are handing out phones — it closes on its own.')}
      </p>

      {error && <div className="notice bad">{error}</div>}

      {q.cold ? <Rows n={1} /> : open ? (
        <>
          <div className="notice">
            <strong>{t('Open')}</strong>{' — '}
            {t('closes in')} {closesIn()}
            {w?.opened_by && <> · {t('opened by')} {w.opened_by}</>}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn" disabled={busy}
              onClick={() => void act(() => closeEnrollWindow(orgId))}>
              {busy ? t('Closing…') : t('Close it now')}
            </button>
          </div>
        </>
      ) : (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {hours.map((h) => (
            <button key={h} className="btn sm" disabled={busy}
              onClick={() => void act(() => openEnrollWindow(orgId, h))}>
              {h} {h === 1 ? t('hour') : t('hours')}
            </button>
          ))}
        </div>
      )}

      {/* Named, not counted. Opening the door also lets in whoever was already
          waiting, and admitting a device somebody had deliberately left in the
          queue for two days is a thing they should see they just did -- here,
          rather than by noticing it in the list afterwards. */}
      {letIn !== null && letIn.length > 0 && (
        <div className="notice">
          {t('Already waiting, and now let in')}: {letIn.join(', ')}
        </div>
      )}
      {skipped.length > 0 && (
        <div className="notice bad">
          {t('Left waiting, because the code they used has since been withdrawn')}:{' '}
          {skipped.join(', ')}
        </div>
      )}
    </section>
  )
}

/* --------------------------------------------------------------- codes */

export function Codes({ orgId }: { orgId: string }) {
  const t = useT()
  const path = `/v1/orgs/${encodeURIComponent(orgId)}/enroll-codes`
  const q = useApi<{ items: EnrollCode[]; window?: EnrollWindowState }>(
    orgId ? path : null)
  const org = useApi<OrgDetail>(orgId ? `/v1/orgs/${encodeURIComponent(orgId)}` : null)
  const [minted, setMinted] = useState<(MintedCode & { label?: string }) | null>(null)
  const [making, setMaking] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)

  const refresh = () => { invalidate(path); q.reload() }

  return (
    <section className="card stack" style={{ gap: 16, marginBottom: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t('Codes')}</h2>
        {!making && (
          <button className="btn primary sm" onClick={() => { setMaking(true); setMinted(null) }}>
            {t('New code')}
          </button>
        )}
      </div>

      {/* Shown once and never again. The server keeps only a hash, so this box
          is the only moment the characters exist anywhere but on the card they
          are about to be written on. Said on screen, so nobody closes it
          expecting to find it in the list afterwards. */}
      {minted && <Minted code={minted} onDone={() => setMinted(null)} />}

      {making && (
        <NewCode
          orgId={orgId}
          org={org.data}
          onCancel={() => setMaking(false)}
          onMade={(m) => { setMinted(m); setMaking(false); refresh() }}
        />
      )}

      <ErrorBox error={error ?? q.error} />

      {q.cold ? <Rows n={3} /> : (q.data?.items ?? []).length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {t('No codes yet. Make one and hand it to whoever does the counting.')}
        </p>
      ) : (
        <div className="stack" style={{ gap: 2 }}>
          {(q.data?.items ?? []).map((c) => (
            <CodeRow key={c.code_id} orgId={orgId} code={c}
              onRotated={(m) => { setMinted(m); refresh() }}
              onChanged={refresh} onError={setError} />
          ))}
        </div>
      )}
    </section>
  )
}

function CodeRow({
  orgId, code, onRotated, onChanged, onError,
}: {
  orgId: string
  code: EnrollCode
  onRotated: (m: MintedCode) => void
  onChanged: () => void
  onError: (e: ApiError) => void
}) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try { await fn() } catch (e) { if (e instanceof ApiError) onError(e) }
    finally { setBusy(false); setConfirming(false) }
  }

  const where = code.scope_name ?? t('the whole company')
  const uses = `${code.uses} ${t('used')}`

  return (
    <div className="device-row">
      <div>
        <div style={{ fontWeight: 600 }}>
          {code.label || t('Unnamed code')}{' '}
          {!code.live && (
            <span className="muted" style={{ fontWeight: 400 }}>
              — {code.revoked_at ? t('revoked')
                 : new Date(code.expires_at) < new Date() ? t('expired')
                 : t('used up')}
            </span>
          )}
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          {code.display_code && (
            <>
              <span style={{
                fontFamily: 'var(--mono, ui-monospace, monospace)',
                letterSpacing: '0.08em', color: 'var(--fg)',
              }}>{code.display_code}</span>
              {' · '}
            </>
          )}
          {where} · {t(ROLE_WORD[code.member_role])} · {uses}
          {code.devices > 0 && ` · ${code.devices} ${t('devices')}`}
        </div>
      </div>

      {confirming ? (
        <div className="stack" style={{ gap: 6, alignItems: 'flex-end' }}>
          <span className="faint" style={{ fontSize: 12, textAlign: 'right' }}>
            {/* The sentence that makes this safe to press. */}
            {code.devices === 0
              ? t('No device is using this code.')
              : `${code.devices} ${t('devices already connected keep working.')}`}
          </span>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn danger sm" disabled={busy}
              onClick={() => void act(async () => {
                await revokeEnrollCode(orgId, code.code_id); onChanged()
              })}>
              {t('Revoke it')}
            </button>
            <button className="btn ghost sm" onClick={() => setConfirming(false)}>
              {t('Cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ gap: 6 }}>
          {code.live && (
            <button className="btn sm" disabled={busy}
              onClick={() => void act(async () => {
                onRotated(await rotateEnrollCode(orgId, code.code_id))
              })}
              title={t('Replace the characters. Devices already connected are untouched.')}>
              {t('Rotate')}
            </button>
          )}
          {!code.revoked_at && (
            <button className="btn ghost sm" onClick={() => setConfirming(true)}>
              {t('Revoke')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** The code, in the clear. Readable again from the list afterwards. */
function Minted({ code, onDone }: { code: MintedCode; onDone: () => void }) {
  const t = useT()
  const [copied, setCopied] = useState(false)
  return (
    <div className="notice stack minted" style={{ gap: 14 }}>
      <div>
        <strong>{t('Here it is.')}</strong>{' '}
        {t('It is in the list below whenever you need it again — it is not a password, and anybody using it still has to be approved by you.')}
      </div>

      <div className="row" style={{ gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="stack" style={{ gap: 8 }}>
          <div style={{
            fontSize: 28, letterSpacing: '0.12em',
            fontFamily: 'var(--mono, ui-monospace, monospace)', fontWeight: 600,
          }}>
            {code.display_code}
          </div>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn sm" onClick={() => {
              void navigator.clipboard?.writeText(code.code).then(() => setCopied(true))
            }}>
              {copied ? t('Copied') : t('Copy')}
            </button>
            <button className="btn sm" onClick={() => window.print()}>
              {t('Print')}
            </button>
          </div>
          <div className="faint" style={{ fontSize: 12 }}>
            {t('Anyone can use it, as many times as they like.')}{' '}
            {t('Good until')} {new Date(code.expires_at).toLocaleDateString()}
          </div>
        </div>

        <div className="stack" style={{ gap: 6, alignItems: 'center' }}>
          <Qr text={code.qr_url} size={190} />
          <span className="faint" style={{ fontSize: 12 }}>{t('Or scan this')}</span>
        </div>
      </div>

      <div>
        <button className="btn ghost sm" onClick={onDone}>{t('Done')}</button>
      </div>
    </div>
  )
}

function NewCode({
  orgId, org, onCancel, onMade,
}: {
  orgId: string
  org: OrgDetail | undefined
  onCancel: () => void
  onMade: (m: MintedCode) => void
}) {
  const t = useT()
  const [label, setLabel] = useState('')
  const [code, setCode] = useState('')
  // What was actually typed, kept only so the form can point out when folding
  // changed it -- "Ljo2" becoming "LJ02" is the one surprise on this screen,
  // and it is much better met here than on a printed card.
  const [rawCode, setRawCode] = useState('')
  const [scope, setScope] = useState('')          // "site:x", "area:y", or ""
  const [role, setRole] = useState<MemberRole>('site_manager')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const [kind, id] = scope.split(':')
    try {
      onMade(await mintEnrollCode(orgId, {
        member_role: role,
        area_id: kind === 'area' ? id : null,
        site_id: kind === 'site' ? id : null,
        label: label.trim() || undefined,
        code,
      }))
    } catch (err) {
      setError(err instanceof ApiError
        ? (err.code === 'code_taken' ? (err.detail ?? err.code)
          : err.code === 'no_manager'
            /* The server refuses rather than inventing a recipient, and the
               reason is worth repeating here: a device that connects in
               silence is the failure this notification exists to prevent. */
            ? t('Nobody would be told when a device connects. Add a company manager first.')
          : (err.detail ?? err.code))
        : t('Could not reach the receiver.'))
      setBusy(false)
    }
  }

  // company_manager is the one role that must NOT name a scope, and every
  // other role sees nothing until it does. Mirrored here so the form cannot
  // build a request the receiver will refuse.
  const needsScope = role !== 'company_manager'

  return (
    <form className="stack" onSubmit={submit}
      style={{ gap: 14, borderLeft: '3px solid var(--line)', paddingLeft: 14 }}>
      {/* THE CODE, CHOSEN RATHER THAN GENERATED.
          It is not a secret and is not defended as one -- every locality a
          Norwegian company owns is in a public register by name, so anything
          short enough to remember is short enough to guess. What keeps a
          guesser out is the approval below, or a registered network. So this
          field optimises for the thing that actually matters: something a
          manager can say over the radio without spelling it. */}
      <Field label="The code"
        hint={t('Short and easy to say. Anyone who has it still has to be let in by you, or be on one of your networks.')}>
        <input
          value={grouped(code)}
          onChange={(e) => {
            setRawCode(e.target.value)
            setCode(canonical(e.target.value))
          }}
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          autoComplete="off"
          autoFocus
          required
          placeholder="LJ02"
          style={{
            fontSize: 22, letterSpacing: '0.12em',
            fontFamily: 'var(--mono, ui-monospace, monospace)',
          }}
        />
      </Field>
      {/* Said while it can still be changed, not discovered on the printed
          card: the alphabet has no I and no O, so they are read back as 1 and
          0. A manager typing "Ljo2" is choosing "LJ02". */}
      {code !== rawCode.toUpperCase().replace(/[^0-9A-Z]/g, '') && code !== '' && (
        <p className="faint" style={{ margin: '-8px 0 0', fontSize: 12 }}>
          {t('Letters I and O are read as 1 and 0, so this code is')}{' '}
          <strong>{grouped(code)}</strong>.
        </p>
      )}

      <Field label="What to call it"
        hint={t('Also what a device is called until somebody types a name on it.')}>
        <input value={label} onChange={(e) => setLabel(e.target.value)}
          maxLength={200} placeholder={t('Merd 3, or Lokalitet Nord')} />
      </Field>

      <Field label="Where it may count">
        <select value={scope} onChange={(e) => {
          setScope(e.target.value)
          // Keep the role honest against the scope rather than letting the
          // server say no after the fact.
          if (!e.target.value) setRole('company_manager')
          else if (role === 'company_manager') {
            setRole(e.target.value.startsWith('area:') ? 'area_manager' : 'site_manager')
          }
        }}>
          <option value="">{t('The whole company')}</option>
          {(org?.areas ?? []).map((a) => (
            <option key={a.area_id} value={`area:${a.area_id}`}>
              {t('Area')}: {a.name}
            </option>
          ))}
          {(org?.sites ?? []).map((s) => (
            <option key={s.site_id} value={`site:${s.site_id}`}>{s.name}</option>
          ))}
        </select>
      </Field>

      {needsScope && (
        <Field label="As"
          hint={t('The role decides which farms this device sees — and therefore how much of the company is on a screen anyone can pick up. What it may change is still only counts and the logbook.')}>
          <select value={role} onChange={(e) => setRole(e.target.value as MemberRole)}>
            {(scope.startsWith('area:')
              ? ['area_manager', 'site_manager', 'ship', 'subsupplier']
              : ['site_manager', 'ship', 'subsupplier']
            ).map((r) => (
              <option key={r} value={r}>{t(ROLE_WORD[r as MemberRole])}</option>
            ))}
          </select>
        </Field>
      )}


      {error && <div className="notice bad">{error}</div>}

      <div className="row" style={{ gap: 8 }}>
        <button className="btn primary" type="submit"
          disabled={busy || code.length < 3}>
          {busy ? t('Making it…') : t('Make the code')}
        </button>
        <button className="btn ghost" type="button" onClick={onCancel}>
          {t('Cancel')}
        </button>
      </div>
    </form>
  )
}

/* --------------------------------------------------------------- networks */

/** The addresses a device may connect from without anybody being asked.
 *
 *  This section is written to be read by somebody who is going to be
 *  disappointed by it, so it says what it is before it says what it does: it
 *  is the public IP address, it is not the wifi name, and it does not work on
 *  mobile data. Every one of those is a question a manager will otherwise ask
 *  after registering a network that never matches anything.
 */
export function Networks({ orgId }: { orgId: string }) {
  const t = useT()
  const [data, setData] = useState<Awaited<ReturnType<typeof listNetworks>> | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  const [label, setLabel] = useState('')

  const load = async () => {
    if (!orgId) return
    try { setData(await listNetworks(orgId)) }
    catch (e) { if (e instanceof ApiError) setError(e) }
  }
  useEffect(() => { void load() }, [orgId])

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null)
    try { await fn(); await load(); setLabel('') }
    catch (e) { if (e instanceof ApiError) setError(e) }
    finally { setBusy(false) }
  }

  const items = data?.items ?? []
  const live = items.filter((n) => n.live)
  const here = data?.your_network

  return (
    <section className="card stack" style={{ gap: 16, marginBottom: 20 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t('Your networks')}</h2>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>
          {t('A device connecting from one of these is let in immediately, with nobody asked. Afterwards it works from anywhere.')}
        </p>
      </div>

      <ErrorBox error={error} />

      {/* One button, and what it will register is on it. A field to type an
          address into exists too, but second: an address typed from memory is
          how somebody silently registers the network they are not on. */}
      {data && (
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="What to call it">
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              maxLength={200} placeholder={t('The office')} />
          </Field>
          <button className="btn primary" disabled={busy || !data.your_ip || !!here}
            onClick={() => void act(() => addNetwork(orgId, {
              label: label.trim() || undefined,
            }))}>
            {here
              ? t('You are already on a registered network')
              : `${t('Add the one I am on')} (${data.your_ip ?? '—'})`}
          </button>
        </div>
      )}

      {live.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {t('None yet. Without one, every new device waits for you to approve it — which is fine, and is the safe default.')}
        </p>
      ) : (
        <div className="stack" style={{ gap: 2 }}>
          {live.map((n) => (
            <NetworkRow key={n.net_id} orgId={orgId} net={n} here={n.net_id === here}
              busy={busy} onAct={act} />
          ))}
        </div>
      )}

      {/* The disappointing part, said plainly rather than discovered. */}
      <p className="faint" style={{ margin: 0, fontSize: 12 }}>
        {t('This is the internet address you appear from, not the wifi name — a web page is never told which network it is on. It works for an office or a base with a fixed line. It does not work over mobile data, where a phone at the farm and a phone in Oslo look the same from here.')}
      </p>
    </section>
  )
}

function NetworkRow({
  orgId, net, here, busy, onAct,
}: {
  orgId: string
  net: EnrollNetwork
  here: boolean
  busy: boolean
  onAct: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const t = useT()
  const [confirming, setConfirming] = useState(false)
  return (
    <div className="device-row">
      <div>
        <div style={{ fontWeight: 600 }}>
          {net.label || t('Unnamed network')}
          {here && (
            <span className="muted" style={{ fontWeight: 400 }}>
              {' '}— {t('you are on this one')}
            </span>
          )}
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          <span style={{ fontFamily: 'var(--mono, ui-monospace, monospace)' }}>
            {net.masklen === 32 || net.masklen === 128 ? net.address : net.net}
          </span>
          {' · '}
          {net.devices === 0
            ? t('no devices yet')
            : `${net.devices} ${t('devices')}`}
          {' · '}{seen(t, net.last_used_at)}
        </div>
      </div>
      {confirming ? (
        <div className="stack" style={{ gap: 6, alignItems: 'flex-end' }}>
          <span className="faint" style={{ fontSize: 12, textAlign: 'right' }}>
            {net.devices === 0
              ? t('No device came in this way.')
              : `${net.devices} ${t('devices already connected keep working.')}`}
          </span>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn danger sm" disabled={busy}
              onClick={() => void onAct(() => revokeNetwork(orgId, net.net_id))}>
              {t('Remove it')}
            </button>
            <button className="btn ghost sm" onClick={() => setConfirming(false)}>
              {t('Cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button className="btn ghost sm" onClick={() => setConfirming(true)}>
          {t('Remove')}
        </button>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- devices */

export function Devices({ orgId }: { orgId: string }) {
  const t = useT()
  const path = `/v1/orgs/${encodeURIComponent(orgId)}/devices`
  const q = useApi<{ items: FieldDevice[] }>(orgId ? path : null)
  const [error, setError] = useState<ApiError | null>(null)
  const refresh = () => { invalidate(path); q.reload() }

  return (
    <section className="card stack" style={{ gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>{t('Connected devices')}</h2>
      <ErrorBox error={error ?? q.error} />
      {q.cold ? <Rows n={3} /> : (q.data?.items ?? []).length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {t('Nothing connected yet.')}
        </p>
      ) : (
        <div className="stack" style={{ gap: 2 }}>
          {(q.data?.items ?? []).map((d) => (
            <DeviceRow key={d.device_id} orgId={orgId} device={d}
              onChanged={refresh} onError={setError} />
          ))}
        </div>
      )}
    </section>
  )
}

function DeviceRow({
  orgId, device, onChanged, onError,
}: {
  orgId: string
  device: FieldDevice
  onChanged: () => void
  onError: (e: ApiError) => void
}) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [name, setName] = useState<string | null>(null)

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try { await fn(); onChanged() } catch (e) { if (e instanceof ApiError) onError(e) }
    finally { setBusy(false); setConfirming(false); setName(null) }
  }

  const where = device.scopes.length === 0
    ? t('the whole company')
    : device.scopes.map((s) => s.name ?? s.id).join(', ')

  /* How it got in, in a few words. This is the question asked about a device
     that turns out not to belong, and the answer has three shapes because the
     three are genuinely different: a person decided, somebody holding the mail
     decided, or nobody did and a registered network let it through. */
  const admitted = device.approved_via === 'network'
    ? `${t('let in by')} ${device.approved_network ?? t('a registered network')}`
    : device.approved_via === 'approval'
      ? `${t('approved by')} ${device.approved_by_name ?? t('a manager')}`
      : device.approved_via === 'link'
        ? t('approved from the email')
        : device.approved_via === 'legacy'
          ? t('connected before approval was required')
          : null

  return (
    <div className="device-row"
      style={device.waiting ? { borderLeft: '3px solid var(--warn, #c98a12)' } : undefined}>
      <div style={{ minWidth: 0 }}>
        {name === null ? (
          <div style={{ fontWeight: 600 }}>
            {device.display_name || device.code_label || device.device_id}
            {!device.display_name && (
              <span className="muted" style={{ fontWeight: 400 }}>
                {' '}— {t('nobody has named it')}
              </span>
            )}
          </div>
        ) : (
          <form className="row" style={{ gap: 6 }} onSubmit={(e) => {
            e.preventDefault()
            void act(() => renameFieldDevice(orgId, device.device_id, name.trim()))
          }}>
            <input value={name} onChange={(e) => setName(e.target.value)}
              maxLength={200} autoFocus />
            <button className="btn sm" type="submit" disabled={busy || !name.trim()}>
              {t('Save')}
            </button>
            <button className="btn ghost sm" type="button" onClick={() => setName(null)}>
              {t('Cancel')}
            </button>
          </form>
        )}
        <div className="muted" style={{ fontSize: 13 }}>
          {device.connected ? `${where} · ` : ''}
          {seen(t, device.last_seen_at)}
          {admitted && ` · ${admitted}`}
          {/* The device id, quietly. It is what the audit trail says, so
              somebody reading a row there has to be able to find it here. */}
          <span className="faint"> · {device.device_id}</span>
        </div>
        {device.waiting && (
          <div style={{ fontSize: 13, marginTop: 2 }}>
            <strong>
              {device.connected
                ? t('Asking for wider access to')
                : t('Asking to connect to')}
            </strong>{' '}
            {device.waiting_for ?? t('this company')}
            {device.last_ip && (
              <span className="faint"> · {t('from')} {device.last_ip}</span>
            )}
          </div>
        )}
      </div>

      {confirming ? (
        <div className="stack" style={{ gap: 6, alignItems: 'flex-end' }}>
          <span className="faint" style={{ fontSize: 12, textAlign: 'right' }}>
            {t('It stops at its next request. Everything it counted stays, still under its name.')}
          </span>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn danger sm" disabled={busy}
              onClick={() => void act(() => unrollDevice(orgId, device.device_id))}>
              {t('Disconnect it')}
            </button>
            <button className="btn ghost sm" onClick={() => setConfirming(false)}>
              {t('Cancel')}
            </button>
          </div>
        </div>
      ) : device.waiting ? (
        /* Approve and decline, not rename and disconnect. A device that has
           never been let in cannot be "disconnected" -- offering that verb
           would be describing the wrong event -- and renaming something before
           deciding whether it belongs is work in the wrong order. */
        <div className="row" style={{ gap: 6 }}>
          <button className="btn primary sm" disabled={busy}
            onClick={() => void act(() => approveDevice(orgId, device.device_id))}>
            {t('Approve')}
          </button>
          <button className="btn ghost sm" disabled={busy}
            onClick={() => void act(() => declineDevice(orgId, device.device_id))}>
            {t('Decline')}
          </button>
        </div>
      ) : (
        <div className="row" style={{ gap: 6 }}>
          <button className="btn ghost sm"
            onClick={() => setName(device.display_name ?? '')}>
            {t('Rename')}
          </button>
          <button className="btn ghost sm" onClick={() => setConfirming(true)}>
            {t('Disconnect')}
          </button>
        </div>
      )}
    </div>
  )
}

function seen(t: (k: string) => string, iso: string | null): string {
  if (!iso) return t('never used')
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days === 0) return t('seen today')
  if (days === 1) return t('seen yesterday')
  return `${t('last seen')} ${days} ${t('days ago')}`
}
